import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, Info, ListCheck, Loader } from "lucide-react";
import {
  getChallanProDetails,
  getVehicleValuationPro,
  verifyPrefillv3Details,
} from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isAlphanumeric, isNumeric } from "validator";

function VehicleValuationPro() {
  const { serviceName } = useService();

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [vehicleNumber, setVehicleNumber] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  // ---------------- VERIFY CONSUMER ----------------
  const handleVerifyConsumer = async () => {
    if (!profileData) return;

    const validate = isAlpha(vehicleNumber);

    if (!validate) {
      return setInputError(
        "⚠️ Please enter alpha numeric in vehicle No. field",
      );
    } 

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getVehicleValuationPro({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        vehicle_num: vehicleNumber.trim(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start py-14">
        {/* ---------------- FORM ---------------- */}
        <div className=" max-w-3xl border rounded-md sm:p-6 mt-6 bg-white">
          <div className="flex items-center flex-col flex-wrap gap-4">
            {/* Vehicle */}
            <div className="flex items-center gap-4">
              <label className="w-32 font-bold">
                Vehicle No <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                maxLength={15}
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: GJ08BN1256"
              />
            </div>

            {/* show input errors */}

            {inputError && (
              <div className="text-red-800 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex justify-end ">
              <button
                disabled={isLoading || !vehicleNumber}
                onClick={handleVerifyConsumer}
                className="px-6 py-2 bg-gradient-to-br hover:bg-black active:bg-green-600 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {/* response message */}

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div className="w-full flex justify-center">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid vehicle number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            <div className="bg-gray-200 p-5 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2 font-bold text-blue-500 mb-1">
                <ListCheck /> Full Vehicle Data
              </h2>
              <hr className="mb-8" />
              {response?.status?.code === 200 && (
                <div className="mb-6 p-4 bg-white rounded-lg shadow">
                  {/* Loop through all fields */}
                  {Object.entries(response?.data).map(([key, value]) => {
                    // ❌ Skip null / empty
                    if (
                      value === null ||
                      value === "" ||
                      (Array.isArray(value) && value.length === 0)
                    ) {
                      return null;
                    }

                    if (key === "errors") {
                      return value.message;
                    }

                    // ✅ Default field renderer
                    return (
                      <div key={key} className="text-sm mb-1">
                        <span className="font-semibold text-gray-600 w-48 inline-block capitalize">
                          {key.replace(/([A-Z])/g, " $1")}:
                        </span>
                        <span className="text-gray-900">
                          {typeof value === "boolean"
                            ? value
                              ? "Yes"
                              : "No"
                            : Array.isArray(value)
                              ? value.join(", ")
                              : typeof value === "object" && value !== null
                                ? JSON.stringify(value)
                                : value}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}{" "}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default VehicleValuationPro;
